/**
 * EventProviderDashboard - Unified dashboard for Event service providers
 * Supports: Venue Owners, DJs/Bands, Caterers, Photographers, Decorators, Planners
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Calendar } from '../../components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import {
  Calendar as CalendarIcon, Clock, DollarSign, Users, Star, MessageSquare,
  CheckCircle, XCircle, AlertCircle, Building, Music, Utensils, Camera,
  Palette, Package, ClipboardList, TrendingUp, Eye, Edit, Trash2, Plus,
  ArrowLeft, RefreshCw, Loader2, Mail, Phone, MapPin, FileText
} from 'lucide-react';
import { format, parseISO, isToday, isTomorrow, addDays } from 'date-fns';
import { toast } from 'sonner';
import GoogleCalendarConnect from '../../components/events/GoogleCalendarConnect';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Provider type configurations
const PROVIDER_CONFIGS = {
  VENUE_OWNER: {
    name: 'Venue Owner',
    icon: Building,
    color: 'purple',
    features: ['calendar', 'bookings', 'packages', 'issues'],
    bookingMode: 'request'
  },
  DJ: {
    name: 'DJ',
    icon: Music,
    color: 'cyan',
    features: ['calendar', 'bookings', 'packages', 'issues'],
    bookingMode: 'instant'
  },
  BAND: {
    name: 'Band',
    icon: Music,
    color: 'violet',
    features: ['calendar', 'bookings', 'packages', 'issues'],
    bookingMode: 'instant'
  },
  CATERER: {
    name: 'Caterer',
    icon: Utensils,
    color: 'orange',
    features: ['quotes', 'menu', 'issues'],
    bookingMode: 'quote'
  },
  PHOTOGRAPHER: {
    name: 'Photographer',
    icon: Camera,
    color: 'pink',
    features: ['calendar', 'bookings', 'portfolio', 'packages', 'issues'],
    bookingMode: 'request'
  },
  DECORATOR: {
    name: 'Decorator',
    icon: Palette,
    color: 'teal',
    features: ['quotes', 'portfolio', 'issues'],
    bookingMode: 'quote'
  },
  RENTAL_SHOP: {
    name: 'Equipment Rental',
    icon: Package,
    color: 'amber',
    features: ['inventory', 'bookings', 'issues'],
    bookingMode: 'instant'
  },
  PLANNER: {
    name: 'Event Planner',
    icon: ClipboardList,
    color: 'blue',
    features: ['quotes', 'packages', 'issues'],
    bookingMode: 'consultation'
  }
};

// Stats Card Component
function StatCard({ title, value, icon: Icon, trend, color = 'gray' }) {
  const colorClasses = {
    purple: 'bg-purple-100 text-purple-600',
    cyan: 'bg-cyan-100 text-cyan-600',
    orange: 'bg-orange-100 text-orange-600',
    green: 'bg-green-100 text-green-600',
    red: 'bg-red-100 text-red-600',
    blue: 'bg-blue-100 text-blue-600',
    gray: 'bg-gray-100 text-gray-600'
  };

  return (
    <Card>
      <CardContent className="pt-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
            {trend && (
              <p className={`text-xs ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {trend > 0 ? '+' : ''}{trend}% vs last month
              </p>
            )}
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Booking Card Component
function BookingCard({ booking, onAction, currencySymbol }) {
  const statusColors = {
    requested: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-green-100 text-green-800',
    completed: 'bg-blue-100 text-blue-800',
    cancelled: 'bg-red-100 text-red-800'
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="pt-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h4 className="font-semibold">{booking.contact?.name || 'Customer'}</h4>
            <p className="text-sm text-gray-500 capitalize">{booking.event_type?.replace(/_/g, ' ')}</p>
          </div>
          <Badge className={statusColors[booking.status] || 'bg-gray-100'}>
            {booking.status}
          </Badge>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-gray-600">
            <CalendarIcon className="w-4 h-4" />
            {booking.event_date && format(parseISO(booking.event_date), 'MMM d, yyyy')}
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <Clock className="w-4 h-4" />
            {booking.start_time} - {booking.end_time}
          </div>
          {booking.guest_count && (
            <div className="flex items-center gap-2 text-gray-600">
              <Users className="w-4 h-4" />
              {booking.guest_count} guests
            </div>
          )}
        </div>

        {booking.status === 'requested' && (
          <div className="flex gap-2 mt-4">
            <Button 
              size="sm" 
              className="flex-1 bg-green-600 hover:bg-green-700"
              onClick={() => onAction(booking.id, 'confirm')}
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              Confirm
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              className="flex-1 text-red-600 border-red-300"
              onClick={() => onAction(booking.id, 'decline')}
            >
              <XCircle className="w-4 h-4 mr-1" />
              Decline
            </Button>
          </div>
        )}

        {booking.status === 'confirmed' && (
          <div className="flex gap-2 mt-4">
            <Button 
              size="sm" 
              variant="outline"
              className="flex-1"
              onClick={() => onAction(booking.id, 'message')}
            >
              <MessageSquare className="w-4 h-4 mr-1" />
              Message
            </Button>
            <Button 
              size="sm" 
              className="flex-1"
              onClick={() => onAction(booking.id, 'complete')}
            >
              Complete
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Quote Card Component (for Caterers/Decorators)
function QuoteCard({ quote, onAction, currencySymbol }) {
  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    quoted: 'bg-blue-100 text-blue-800',
    accepted: 'bg-green-100 text-green-800',
    declined: 'bg-red-100 text-red-800'
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="pt-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h4 className="font-semibold">{quote.contact?.name || 'Customer'}</h4>
            <p className="text-sm text-gray-500 capitalize">{quote.event_type?.replace(/_/g, ' ')}</p>
          </div>
          <Badge className={statusColors[quote.status] || 'bg-gray-100'}>
            {quote.status}
          </Badge>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-gray-600">
            <CalendarIcon className="w-4 h-4" />
            {quote.event_date && format(parseISO(quote.event_date), 'MMM d, yyyy')}
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <Users className="w-4 h-4" />
            {quote.guest_count} guests
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <DollarSign className="w-4 h-4" />
            Budget: {quote.budget_per_head}/head
          </div>
          {quote.service_type && (
            <div className="flex items-center gap-2 text-gray-600">
              <Utensils className="w-4 h-4" />
              {quote.service_type.replace(/_/g, ' ')}
            </div>
          )}
        </div>

        {quote.dietary_requirements?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {quote.dietary_requirements.map(req => (
              <Badge key={req} variant="outline" className="text-xs capitalize">
                {req.replace(/_/g, ' ')}
              </Badge>
            ))}
          </div>
        )}

        {quote.status === 'pending' && (
          <div className="flex gap-2 mt-4">
            <Button 
              size="sm" 
              className="flex-1 bg-orange-600 hover:bg-orange-700"
              onClick={() => onAction(quote.id, 'send_quote')}
            >
              <FileText className="w-4 h-4 mr-1" />
              Send Quote
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => onAction(quote.id, 'view')}
            >
              <Eye className="w-4 h-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Issue Card Component
function IssueCard({ issue, onResolve }) {
  const statusColors = {
    open: 'bg-red-100 text-red-800',
    in_progress: 'bg-yellow-100 text-yellow-800',
    resolution_offered: 'bg-blue-100 text-blue-800',
    resolved: 'bg-green-100 text-green-800'
  };

  const typeIcons = {
    service_quality: AlertCircle,
    late_arrival: Clock,
    wrong_order: XCircle,
    damage: AlertCircle,
    communication: MessageSquare,
    other: FileText
  };

  const Icon = typeIcons[issue.issue_type] || AlertCircle;

  return (
    <Card className="border-l-4 border-l-red-500">
      <CardContent className="pt-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Icon className="w-5 h-5 text-red-500" />
            <div>
              <h4 className="font-semibold capitalize">{issue.issue_type?.replace(/_/g, ' ')}</h4>
              <p className="text-xs text-gray-500">
                Booking #{issue.booking_id?.slice(0, 8)}
              </p>
            </div>
          </div>
          <Badge className={statusColors[issue.status]}>
            {issue.status?.replace(/_/g, ' ')}
          </Badge>
        </div>

        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{issue.description}</p>

        <div className="text-sm text-gray-500 mb-3">
          <span>Preferred: </span>
          <span className="font-medium capitalize">{issue.preferred_resolution?.replace(/_/g, ' ')}</span>
        </div>

        {issue.status === 'open' && (
          <Button 
            size="sm" 
            className="w-full"
            onClick={() => onResolve(issue)}
          >
            Respond to Issue
          </Button>
        )}

        {issue.status === 'resolution_offered' && issue.resolution && (
          <div className="p-2 bg-blue-50 rounded text-sm">
            <p className="text-blue-800">
              <strong>Your offer:</strong> {issue.resolution.type?.replace(/_/g, ' ')}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Calendar View Component
function CalendarView({ bookings, onDateSelect }) {
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // Get booking dates for highlighting
  const bookedDates = bookings
    .filter(b => b.event_date)
    .map(b => new Date(b.event_date));

  const handleSelect = (date) => {
    setSelectedDate(date);
    onDateSelect?.(date);
  };

  // Get bookings for selected date
  const selectedDateBookings = bookings.filter(b => 
    b.event_date && format(parseISO(b.event_date), 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd')
  );

  return (
    <div className="flex gap-6">
      <div>
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleSelect}
          modifiers={{
            booked: bookedDates
          }}
          modifiersStyles={{
            booked: { backgroundColor: '#ddd6fe', color: '#7c3aed' }
          }}
          className="rounded-md border"
        />
      </div>
      <div className="flex-1">
        <h4 className="font-semibold mb-3">
          {format(selectedDate, 'EEEE, MMMM d, yyyy')}
        </h4>
        {selectedDateBookings.length > 0 ? (
          <div className="space-y-2">
            {selectedDateBookings.map(booking => (
              <div key={booking.id} className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                <p className="font-medium">{booking.contact?.name}</p>
                <p className="text-sm text-gray-600">
                  {booking.start_time} - {booking.end_time} | {booking.event_type}
                </p>
                <Badge className="mt-1" variant="outline">{booking.status}</Badge>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">No bookings for this date</p>
        )}
      </div>
    </div>
  );
}

// Main Dashboard Component
export default function EventProviderDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Mock provider data (in production, fetch from auth/session)
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  const [bookings, setBookings] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [issues, setIssues] = useState([]);
  const [stats, setStats] = useState({});
  
  const [resolveModalOpen, setResolveModalOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);

  const providerId = searchParams.get('id') || '0a10bb7b-f4e6-4a8b-b52a-985dc8eb0530'; // Default for demo
  const currencySymbol = 'Rs.';

  // Fetch provider and data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch provider details
        const providerRes = await fetch(`${API_URL}/api/events/providers/${providerId}`);
        if (providerRes.ok) {
          const providerData = await providerRes.json();
          setProvider(providerData);
        }

        // Fetch bookings
        const bookingsRes = await fetch(`${API_URL}/api/events/bookings?provider_id=${providerId}`);
        if (bookingsRes.ok) {
          const bookingsData = await bookingsRes.json();
          setBookings(bookingsData.bookings || []);
        }

        // Fetch quotes (for caterers)
        const quotesRes = await fetch(`${API_URL}/api/events/quotes?provider_id=${providerId}`);
        if (quotesRes.ok) {
          const quotesData = await quotesRes.json();
          setQuotes(quotesData.quotes || []);
        }

        // Fetch issues
        const issuesRes = await fetch(`${API_URL}/api/events/issues?provider_id=${providerId}`);
        if (issuesRes.ok) {
          const issuesData = await issuesRes.json();
          setIssues(issuesData.issues || []);
        }

        // Calculate stats
        setStats({
          totalBookings: bookings.length,
          pendingBookings: bookings.filter(b => b.status === 'requested').length,
          confirmedBookings: bookings.filter(b => b.status === 'confirmed').length,
          pendingQuotes: quotes.filter(q => q.status === 'pending').length,
          openIssues: issues.filter(i => i.status === 'open').length
        });

      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
        toast.error('Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [providerId]);

  // Handle booking actions
  const handleBookingAction = async (bookingId, action) => {
    try {
      if (action === 'confirm') {
        // API call to confirm booking
        toast.success('Booking confirmed!');
        setBookings(prev => prev.map(b => 
          b.id === bookingId ? { ...b, status: 'confirmed' } : b
        ));
      } else if (action === 'decline') {
        toast.success('Booking declined');
        setBookings(prev => prev.map(b => 
          b.id === bookingId ? { ...b, status: 'cancelled' } : b
        ));
      } else if (action === 'complete') {
        toast.success('Booking marked as complete');
        setBookings(prev => prev.map(b => 
          b.id === bookingId ? { ...b, status: 'completed' } : b
        ));
      }
    } catch (error) {
      toast.error('Action failed');
    }
  };

  // Handle quote actions
  const handleQuoteAction = (quoteId, action) => {
    if (action === 'send_quote') {
      const quote = quotes.find(q => q.id === quoteId);
      setSelectedQuote(quote);
      setQuoteModalOpen(true);
    }
  };

  // Handle issue resolution
  const handleResolveIssue = (issue) => {
    setSelectedIssue(issue);
    setResolveModalOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  const config = PROVIDER_CONFIGS[provider?.provider_type] || PROVIDER_CONFIGS.VENUE_OWNER;
  const Icon = config.icon;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className={`bg-gradient-to-r from-${config.color}-600 to-${config.color}-800 text-white py-6 px-4`} 
           style={{ background: `linear-gradient(to right, var(--${config.color}-600, #7c3aed), var(--${config.color}-800, #5b21b6))` }}>
        <div className="max-w-7xl mx-auto">
          <button 
            onClick={() => navigate('/events')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Events
          </button>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-lg">
                <Icon className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">{provider?.provider_name}</h1>
                <p className="opacity-80">{config.name} Dashboard</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Badge className="bg-white/20 text-white border-0">
                <Star className="w-4 h-4 mr-1 fill-yellow-400 text-yellow-400" />
                {provider?.rating?.toFixed(1)}
              </Badge>
              <Button variant="secondary" size="sm">
                <Edit className="w-4 h-4 mr-1" />
                Edit Profile
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {config.bookingMode !== 'quote' && (
            <>
              <StatCard 
                title="Pending Requests" 
                value={bookings.filter(b => b.status === 'requested').length}
                icon={Clock}
                color="orange"
              />
              <StatCard 
                title="Confirmed" 
                value={bookings.filter(b => b.status === 'confirmed').length}
                icon={CheckCircle}
                color="green"
              />
            </>
          )}
          {config.bookingMode === 'quote' && (
            <>
              <StatCard 
                title="Pending Quotes" 
                value={quotes.filter(q => q.status === 'pending').length}
                icon={FileText}
                color="orange"
              />
              <StatCard 
                title="Quotes Sent" 
                value={quotes.filter(q => q.status === 'quoted').length}
                icon={CheckCircle}
                color="blue"
              />
            </>
          )}
          <StatCard 
            title="Open Issues" 
            value={issues.filter(i => i.status === 'open').length}
            icon={AlertCircle}
            color={issues.filter(i => i.status === 'open').length > 0 ? 'red' : 'gray'}
          />
          <StatCard 
            title="This Month" 
            value={`${currencySymbol}${(provider?.starting_price * 5 || 0).toLocaleString()}`}
            icon={TrendingUp}
            color="purple"
            trend={12}
          />
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            {config.features.includes('calendar') && (
              <TabsTrigger value="calendar">Calendar</TabsTrigger>
            )}
            {config.features.includes('bookings') && (
              <TabsTrigger value="bookings">
                Bookings
                {bookings.filter(b => b.status === 'requested').length > 0 && (
                  <Badge className="ml-2 bg-orange-500">{bookings.filter(b => b.status === 'requested').length}</Badge>
                )}
              </TabsTrigger>
            )}
            {config.features.includes('quotes') && (
              <TabsTrigger value="quotes">
                Quote Inbox
                {quotes.filter(q => q.status === 'pending').length > 0 && (
                  <Badge className="ml-2 bg-orange-500">{quotes.filter(q => q.status === 'pending').length}</Badge>
                )}
              </TabsTrigger>
            )}
            {config.features.includes('packages') && (
              <TabsTrigger value="packages">Packages</TabsTrigger>
            )}
            <TabsTrigger value="issues">
              Issues
              {issues.filter(i => i.status === 'open').length > 0 && (
                <Badge className="ml-2 bg-red-500">{issues.filter(i => i.status === 'open').length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Upcoming Bookings */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CalendarIcon className="w-5 h-5" />
                    Upcoming Bookings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {bookings.filter(b => b.status === 'confirmed').length > 0 ? (
                    <div className="space-y-3">
                      {bookings.filter(b => b.status === 'confirmed').slice(0, 3).map(booking => (
                        <div key={booking.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium">{booking.contact?.name}</p>
                            <p className="text-sm text-gray-500">
                              {booking.event_date && format(parseISO(booking.event_date), 'MMM d')} | {booking.event_type}
                            </p>
                          </div>
                          <Button size="sm" variant="outline">View</Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No upcoming bookings</p>
                  )}
                </CardContent>
              </Card>

              {/* Recent Activity */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <RefreshCw className="w-5 h-5" />
                    Recent Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {bookings.slice(0, 5).map(booking => (
                      <div key={booking.id} className="flex items-center gap-3 text-sm">
                        <div className={`w-2 h-2 rounded-full ${
                          booking.status === 'confirmed' ? 'bg-green-500' :
                          booking.status === 'requested' ? 'bg-yellow-500' : 'bg-gray-400'
                        }`} />
                        <span className="flex-1">
                          {booking.status === 'requested' ? 'New booking request' :
                           booking.status === 'confirmed' ? 'Booking confirmed' : 'Booking updated'}
                        </span>
                        <span className="text-gray-400">
                          {booking.created_at && format(parseISO(booking.created_at), 'MMM d')}
                        </span>
                      </div>
                    ))}
                    {bookings.length === 0 && (
                      <p className="text-gray-500 text-center py-4">No recent activity</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Calendar Tab */}
          <TabsContent value="calendar">
            <Card>
              <CardHeader>
                <CardTitle>Availability Calendar</CardTitle>
                <CardDescription>View and manage your bookings</CardDescription>
              </CardHeader>
              <CardContent>
                <CalendarView bookings={bookings} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {bookings.length > 0 ? (
                bookings.map(booking => (
                  <BookingCard 
                    key={booking.id} 
                    booking={booking} 
                    onAction={handleBookingAction}
                    currencySymbol={currencySymbol}
                  />
                ))
              ) : (
                <Card className="col-span-full p-8 text-center">
                  <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No bookings yet</p>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Quotes Tab (for Caterers) */}
          <TabsContent value="quotes">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {quotes.length > 0 ? (
                quotes.map(quote => (
                  <QuoteCard 
                    key={quote.id} 
                    quote={quote} 
                    onAction={handleQuoteAction}
                    currencySymbol={currencySymbol}
                  />
                ))
              ) : (
                <Card className="col-span-full p-8 text-center">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No quote requests yet</p>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Packages Tab */}
          <TabsContent value="packages">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Your Packages</CardTitle>
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Package
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {provider?.packages?.map((pkg, idx) => (
                    <Card key={idx} className="border-2 border-dashed">
                      <CardContent className="pt-4">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-bold">{pkg.name}</h4>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost"><Edit className="w-4 h-4" /></Button>
                            <Button size="sm" variant="ghost" className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </div>
                        <p className="text-2xl font-bold text-purple-600">
                          {currencySymbol}{pkg.price?.toLocaleString()}
                        </p>
                        <p className="text-sm text-gray-500">{pkg.duration}</p>
                        {pkg.includes && (
                          <ul className="mt-2 text-sm text-gray-600">
                            {pkg.includes.map((item, i) => (
                              <li key={i} className="flex items-center gap-1">
                                <CheckCircle className="w-3 h-3 text-green-500" /> {item}
                              </li>
                            ))}
                          </ul>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                  {(!provider?.packages || provider.packages.length === 0) && (
                    <Card className="border-2 border-dashed col-span-full p-8 text-center">
                      <Plus className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">Add your first package</p>
                    </Card>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Issues Tab */}
          <TabsContent value="issues">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {issues.length > 0 ? (
                issues.map(issue => (
                  <IssueCard 
                    key={issue.id} 
                    issue={issue} 
                    onResolve={handleResolveIssue}
                  />
                ))
              ) : (
                <Card className="col-span-full p-8 text-center">
                  <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-3" />
                  <p className="text-gray-500">No issues! Great job maintaining quality service.</p>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="grid md:grid-cols-2 gap-6">
              <GoogleCalendarConnect 
                providerId={providerId}
                onStatusChange={(status) => {
                  if (status.connected) {
                    toast.success('Calendar sync enabled');
                  }
                }}
              />
              
              <Card>
                <CardHeader>
                  <CardTitle>Notification Preferences</CardTitle>
                  <CardDescription>Configure how you receive booking notifications</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">SMS Notifications</p>
                      <p className="text-sm text-gray-500">Receive SMS for new bookings</p>
                    </div>
                    <Badge className="bg-green-100 text-green-700">Active</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Email Notifications</p>
                      <p className="text-sm text-gray-500">Receive email for new bookings</p>
                    </div>
                    <Badge className="bg-green-100 text-green-700">Active</Badge>
                  </div>
                </CardContent>
              </Card>

              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle>Availability Settings</CardTitle>
                  <CardDescription>Set your working hours and maximum bookings per day</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium mb-2">Working Hours Start</p>
                      <Input type="time" defaultValue="09:00" />
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-2">Working Hours End</p>
                      <Input type="time" defaultValue="22:00" />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-2">Maximum Events Per Day</p>
                    <Select defaultValue="2">
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 event</SelectItem>
                        <SelectItem value="2">2 events</SelectItem>
                        <SelectItem value="3">3 events</SelectItem>
                        <SelectItem value="4">4 events</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button className="w-full">Save Settings</Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Issue Resolution Modal */}
      <IssueResolveModal
        open={resolveModalOpen}
        onClose={() => setResolveModalOpen(false)}
        issue={selectedIssue}
        onSubmit={async (resolution) => {
          try {
            const response = await fetch(`${API_URL}/api/events/issues/resolve`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(resolution)
            });
            if (response.ok) {
              toast.success('Resolution sent to customer');
              setResolveModalOpen(false);
              // Refresh issues
              setIssues(prev => prev.map(i => 
                i.id === selectedIssue.id ? { ...i, status: 'resolution_offered' } : i
              ));
            }
          } catch (error) {
            toast.error('Failed to send resolution');
          }
        }}
      />

      {/* Quote Modal */}
      <SendQuoteModal
        open={quoteModalOpen}
        onClose={() => setQuoteModalOpen(false)}
        quote={selectedQuote}
        currencySymbol={currencySymbol}
        onSubmit={async (quoteData) => {
          try {
            toast.success('Quote sent to customer');
            setQuoteModalOpen(false);
            setQuotes(prev => prev.map(q => 
              q.id === selectedQuote.id ? { ...q, status: 'quoted' } : q
            ));
          } catch (error) {
            toast.error('Failed to send quote');
          }
        }}
      />
    </div>
  );
}

// Issue Resolution Modal Component
function IssueResolveModal({ open, onClose, issue, onSubmit }) {
  const [resolutionType, setResolutionType] = useState('');
  const [message, setMessage] = useState('');
  const [refundAmount, setRefundAmount] = useState('');

  if (!issue) return null;

  const handleSubmit = () => {
    onSubmit({
      issue_id: issue.id,
      resolution_type: resolutionType,
      resolution_details: resolutionType === 'partial_refund' ? { amount: refundAmount } : null,
      message
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Respond to Issue</DialogTitle>
          <DialogDescription>
            Offer a resolution to the customer's complaint
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="p-3 bg-red-50 rounded-lg">
            <p className="text-sm font-medium text-red-800 capitalize">{issue.issue_type?.replace(/_/g, ' ')}</p>
            <p className="text-sm text-red-600 mt-1">{issue.description}</p>
          </div>

          <div>
            <label className="text-sm font-medium">Resolution Type</label>
            <Select value={resolutionType} onValueChange={setResolutionType}>
              <SelectTrigger>
                <SelectValue placeholder="Select resolution" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="reschedule">Offer Reschedule</SelectItem>
                <SelectItem value="partial_refund">Partial Refund</SelectItem>
                <SelectItem value="full_refund">Full Refund</SelectItem>
                <SelectItem value="discount_future">Discount on Future Booking</SelectItem>
                <SelectItem value="apology">Apology & Explanation</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {resolutionType === 'partial_refund' && (
            <div>
              <label className="text-sm font-medium">Refund Amount</label>
              <Input
                type="number"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                placeholder="Enter amount"
              />
            </div>
          )}

          <div>
            <label className="text-sm font-medium">Message to Customer</label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Explain your resolution offer..."
              rows={4}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit}
            disabled={!resolutionType || !message}
          >
            Send Resolution
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Send Quote Modal Component
function SendQuoteModal({ open, onClose, quote, currencySymbol, onSubmit }) {
  const [totalPrice, setTotalPrice] = useState('');
  const [pricePerHead, setPricePerHead] = useState('');
  const [menuItems, setMenuItems] = useState('');
  const [notes, setNotes] = useState('');
  const [validDays, setValidDays] = useState('7');

  if (!quote) return null;

  const handleSubmit = () => {
    onSubmit({
      quote_id: quote.id,
      total_price: totalPrice,
      price_per_head: pricePerHead,
      menu_items: menuItems,
      notes,
      valid_days: validDays
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Send Quote</DialogTitle>
          <DialogDescription>
            Respond to {quote.contact?.name}'s catering request
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Request Summary */}
          <div className="p-3 bg-orange-50 rounded-lg text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div><span className="text-gray-500">Guests:</span> {quote.guest_count}</div>
              <div><span className="text-gray-500">Budget:</span> {quote.budget_per_head}/head</div>
              <div><span className="text-gray-500">Service:</span> {quote.service_type}</div>
              <div><span className="text-gray-500">Date:</span> {quote.event_date}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Total Price</label>
              <Input
                type="number"
                value={totalPrice}
                onChange={(e) => setTotalPrice(e.target.value)}
                placeholder={`${currencySymbol}0`}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Price Per Head</label>
              <Input
                type="number"
                value={pricePerHead}
                onChange={(e) => setPricePerHead(e.target.value)}
                placeholder={`${currencySymbol}0`}
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Proposed Menu</label>
            <Textarea
              value={menuItems}
              onChange={(e) => setMenuItems(e.target.value)}
              placeholder="List the menu items included..."
              rows={4}
            />
          </div>

          <div>
            <label className="text-sm font-medium">Additional Notes</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any terms, conditions, or notes..."
              rows={2}
            />
          </div>

          <div>
            <label className="text-sm font-medium">Quote Valid For</label>
            <Select value={validDays} onValueChange={setValidDays}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="3">3 days</SelectItem>
                <SelectItem value="7">7 days</SelectItem>
                <SelectItem value="14">14 days</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit}
            disabled={!totalPrice || !menuItems}
            className="bg-orange-600 hover:bg-orange-700"
          >
            Send Quote
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
